import React from 'react';
import { v4 } from 'uuid';
import DocumentItemsFormElement, { DocumentItemsFormElementSharedProps } from './DocumentItemsFormElement';
import {
  fromWorkOrderItem, Item, DocumentAvailability, bindingLinePrice, suggestedResalePrice,
} from '../models/item';
import { WorkOrderItem } from '../models/workOrder';
import usePricingSettings from '../hooks/usePricingSettings';
import { DEFAULT_PRICING_SETTINGS } from '../models/pricingSettings';
import useUsers from '../hooks/useUsers'; // item 1335 — per-line "Performed by"
import { AccessRole } from '../models/user';

const WorkOrderItemsFormElement: React.FC<DocumentItemsFormElementSharedProps<WorkOrderItem>> = (props) => {
  // item 234 (reworked) — a sales order's binding line price is the GROUP price; the
  // catalog retail becomes the MSRP/SRP suggestion (display only).
  const { pricing } = usePricingSettings();
  const defaults = pricing ?? DEFAULT_PRICING_SETTINGS;
  // item 1335 (Chad: "work order lines need the who column") — same pick list as the invoice
  // (item 943): internal employees only. Captured here, where who-did-the-work is first known;
  // it carries onto the generated invoice line, where the 943 finalize gate still enforces it.
  const { data: usersData } = useUsers();
  const performedByUsers = (usersData?.index ?? [])
    .filter((u: any) => u.isActive !== false && Number(u.accessRole) !== AccessRole.CustomerBuilder)
    .map((u: any) => ({ id: u.id, name: u.name ?? '' }))
    .sort((a: any, b: any) => a.name.localeCompare(b.name));

  const makeItemFromCatalog = (newItem: Item, category: string): WorkOrderItem => ({
    employee: undefined,
    // item 422 — hardware: marked-up group price (never cost); services: the entered
    // group price. MSRP: hardware cost × MSRP markup; services group price × group markup.
    retailEach: bindingLinePrice(newItem),
    listPrice: suggestedResalePrice(newItem, defaults.groupMarkup, defaults.msrpMarkup),
    isHardware: newItem.isHardware ?? false,
    totalHours: '',
    workPerformed: '',
    // item 1488 (Chad) — a line is dated the day it's added (local date, editable in the DATE
    // column); the date always prints on the PDF
    lineDate: new Date().toLocaleDateString('en-CA'),
    id: v4(),
    itemId: newItem.id,
    brand: newItem.brand,
    category,
    costEach: newItem.costEach,
    description: newItem.description,
    quantity: 1,
    sku: newItem.sku,
    taxable: false, // dart (2026-07-15, Chad) — per-line "Add tax" opt-in; default OFF (retired the catalog taxable flag)
    vendorId: newItem.vendorId,
  });

  return (
    <DocumentItemsFormElement<WorkOrderItem>
      categoryNames={props.categoryNames}
      items={props.items}
      setItems={props.setItems}
      categoryButtonHandler={props.categoryButtonHandler}
      activeCategory={props.activeCategory}
      categoryEditMode={props.categoryEditMode}
      editCategoryName={props.editCategoryName}
      setEditCategoryName={props.setEditCategoryName}
      onAddClose={props.onAddClose}
      isAddOpen={props.isAddOpen}
      showRetail
      showMsrpPair
      defaultMsrpMult={defaults.msrpMarkup}
      vendorsOnly
      allowGroupDiscount // item 1234 (Chad) — WO/SO % off group pricing (services + hardware)
      performedByUsers={performedByUsers} // item 1335 — the WHO column
      showLineDate // item 1336 — the per-line T&M date
      availableOn={DocumentAvailability.WorkOrder}
      makeItemFromCatalog={makeItemFromCatalog}
      toCatalogItem={fromWorkOrderItem}
    />
  );
};

export default WorkOrderItemsFormElement;
